import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Printer } from "lucide-react";
import { VN_TIME_ZONE, labelTexts, normalizeLabelSize, type LabelContent, type ProductSummaryDto } from "@hisense/shared";
import { useSettings } from "@/api/hooks";
import { labelRef } from "@/components/domain/LabelPrintButton";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { LabelPreview } from "./LabelPreview";
import { LabelLanguageToggle, useLabelLanguage } from "@/components/domain/LabelLanguageToggle";
import { buildLabelDocument, labelBarcodeFits } from "@/lib/labelDocument";
import { printHtml } from "@/lib/printWindow";

export function ProductBatchPrintButton({ products, disabled }: { products: ProductSummaryDto[]; disabled?: boolean }) {
  const { t } = useTranslation();
  const { data: settings, isLoading, error: settingsError } = useSettings();
  const [open, setOpen] = useState(false);
  const [copies, setCopies] = useState("1");
  const [error, setError] = useState(false);
  // Ngôn ngữ trên tem chọn lúc in (Roary 12/10/2026) — thay cho cờ "Mô tả chính" trên từng sản phẩm.
  const [language, setLanguage] = useLabelLanguage();
  const size = normalizeLabelSize(settings?.labelPrinter ?? {});
  const count = Number(copies);
  const valid = /^\d+$/.test(copies) && Number.isInteger(count) && count >= 1 && count <= 99;
  const contents: LabelContent[] = products.map((p) => ({ productRef: labelRef(p.productCode, p.subRef), ...labelTexts(p, language),
    size: p.size, barcode: labelRef(p.productCode, p.subRef), count: valid ? count : 1,
    footer: `Hisense Button  ${new Date().toLocaleDateString("en-GB", { timeZone: VN_TIME_ZONE })}` }));
  const barcodesFit = "size" in size && contents.every((content) => labelBarcodeFits(size.size, content.barcode));
  return <>
    <Button variant="secondary" size="sm" disabled={disabled || !products.length}
      onClick={() => { setCopies("1"); setError(false); setOpen(true); }}>
      <Printer className="size-4" /> {t("productWorkspace.selectedLabels", { count: products.length })}
    </Button>
    {open && <Modal title={t("productWorkspace.selectedLabels", { count: products.length })} closeLabel={t("common.close")} onClose={() => setOpen(false)}>
      <div className="min-h-0 space-y-4 overflow-auto p-5">
        <div className="max-w-xs"><FieldLabel htmlFor="batch-label-copies">{t("productWorkspace.labelsPerProduct")}</FieldLabel>
          <Input id="batch-label-copies" inputMode="numeric" value={copies} onChange={(e) => setCopies(e.target.value)} invalid={!valid} />
        </div>
        {!valid && <p role="alert" className="text-sm text-danger">{t("productWorkspace.invalidLabelCount")}</p>}
        <p className="font-mono tabular text-sm">{t("productWorkspace.totalLabels", { products: products.length, labels: valid ? count * products.length : 0 })}</p>
        <LabelLanguageToggle value={language} onChange={setLanguage} />
        {"invalid" in size ? <p role="alert" className="text-sm text-danger">{t("label.error.badSize", { field: size.invalid })}</p> :
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">{contents.map((content, i) => <LabelPreview key={products[i]?.id} size={size.size} content={content} />)}</div>}
        {(error || settingsError) && <p role="alert" className="text-sm text-danger">{t("productWorkspace.printError")}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-line bg-surface p-4">
        <Button variant="secondary" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
        <Button disabled={disabled || !products.length || !barcodesFit || !valid || "invalid" in size || isLoading || !!settingsError} onClick={() => {
          if (disabled || !products.length || !barcodesFit || !valid || "invalid" in size) return;
          try { printHtml(buildLabelDocument(size.size, contents)); setOpen(false); } catch { setError(true); }
        }}>{t("productWorkspace.printSelected")}</Button>
      </div>
    </Modal>}
  </>;
}
